/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util (pure availability-filtering helpers)
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      - (no imports; every input is an explicit parameter)
 *   siblings:  booking-request.js, booking-validation.js (other controllers/index.js extracts)
 *   tests:     wave16-coverage-test.js, wave17-coverage-test.js (via controller:index getters
 *              that wrap these functions: hasNoAvailableSlots, isItaliano/isItalianAddress,
 *              contactsFiltered/productsFiltered → filterContacts/filterProducts/filterProductsByContact),
 *              bookable-categories-test.js (bookableCategories)
 *
 * Pure functions carved out of controllers/index.js for filtering
 * operators/products by category/product/operator selection and for
 * availability checks (hasNoAvailableSlots, isItalianAddress). No `this`, no
 * services.
 */
// Pure availability-filtering helpers carved out of controllers/index.js.
// No `this`, no services: every input is an explicit parameter.

// Narrows the operator list by the selected category, then by the selected
// product, decorating the survivors with the first_dispo/stanza pair parsed out
// of the matching product_objs entry.
export function filterContacts(dispos, categorySelected, productSelected) {
  if (!dispos || !Array.isArray(dispos.contacts)) {
    return [];
  }

  let contacts = [...dispos.contacts];
  contacts.forEach((contact) => (contact.push = false));

  if (categorySelected?.id && Array.isArray(dispos.products)) {
    let enabledContacts = [];

    for (let i = 0; i < contacts.length; i++) {
      let contact = contacts[i];

      if (Array.isArray(contact.products)) {
        for (let j = 0; j < contact.products.length; j++) {
          let product = dispos.products.find((p) => p.id === contact.products[j]);

          if (
            product &&
            Array.isArray(product.category_ids) &&
            product.category_ids.includes(categorySelected.id)
          ) {
            if (!contact.push) {
              enabledContacts.push(contact);
              contact.push = true;
            }
            break;
          }
        }
      }
    }

    contacts = enabledContacts;
  }

  if (!productSelected) {
    return contacts;
  }

  let filteredContacts = contacts.filter(
    (contact) => Array.isArray(contact.products) && contact.products.includes(productSelected.id)
  );

  for (let i = 0; i < filteredContacts.length; i++) {
    let contact = filteredContacts[i];
    if (Array.isArray(contact.product_objs)) {
      let productObjSelect = contact.product_objs.find((p) => p.id === productSelected.id);

      if (productObjSelect && productObjSelect.first_dispo) {
        let [firstDispo, stanza] = productObjSelect.first_dispo.split(' @ ');
        contact.first_dispo = firstDispo;
        contact.stanza = stanza;
      }
    }
  }

  return filteredContacts;
}

// Keeps only the products the selected operator can deliver, decorating them
// with the first_dispo/stanza pair parsed out of the matching contact_objs entry.
export function filterProductsByContact(products, contactSelected) {
  if (!contactSelected) {
    return products;
  }

  const filteredProducts = products.filter(
    (p) => Array.isArray(p.contacts) && p.contacts.includes(contactSelected.id)
  );

  for (let i = 0; i < filteredProducts.length; i++) {
    const contactObjs = filteredProducts[i].contact_objs;

    if (Array.isArray(contactObjs)) {
      const contactObjSelect = contactObjs.find((c) => c.id === contactSelected.id);

      if (contactObjSelect && contactObjSelect.first_dispo) {
        const [firstDispo, stanza] = contactObjSelect.first_dispo.split(' @ ');
        filteredProducts[i].first_dispo = firstDispo;
        filteredProducts[i].stanza = stanza;
      }
    }
  }

  return filteredProducts;
}

// The categories worth offering in step 0: those that contain at least one of
// the bookable products the dispos payload returned. dispos_data lists every
// product category of the tenant, including the ones whose products are not
// sold online (or that have no products at all); offering one of those leads
// the patient to an empty product list, which reads as "booking is broken"
// (POP-530). A product is matched through its own category_ids, the same key
// filterProducts uses below, so a category is offered iff picking it yields a
// non-empty list.
export function bookableCategories(dispos) {
  if (!dispos || !Array.isArray(dispos.product_categories) || !Array.isArray(dispos.products)) {
    return [];
  }

  return dispos.product_categories.filter((category) =>
    dispos.products.some(
      (p) => Array.isArray(p.category_ids) && p.category_ids.includes(category.id)
    )
  );
}

// Narrows the product list by the selected category, then by the selected operator.
export function filterProducts(dispos, categorySelected, contactSelected) {
  if (!dispos || !Array.isArray(dispos.products)) {
    return [];
  }

  const products = dispos.products;

  if (categorySelected?.id) {
    const filteredByCategory = products.filter(
      (p) => Array.isArray(p.category_ids) && p.category_ids.includes(categorySelected.id)
    );
    return filterProductsByContact(filteredByCategory, contactSelected);
  }

  return filterProductsByContact(products, contactSelected);
}

// True when there is nothing bookable in the currently selected day.
export function hasNoAvailableSlots(slots) {
  if (!slots || slots.length === 0) return true;
  return slots.every((s) => s.busy);
}

// Country name takes priority: if set to a non-Italian value, CF is never required.
// Matches the logic in form-step-dati.checkRequired and checkin.checkRequired.
export function isItalianAddress(address) {
  const country = address?.location_address_country;
  const countryCode = address?.location_address_country_code;
  if (country && country !== 'Italia') return false;
  return country === 'Italia' || countryCode === 'IT' || countryCode === 'ITA';
}
